/** @format */

import { Component, EventEmitter, Input, OnInit, OnChanges, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, FormControl, UntypedFormGroup } from '@angular/forms';
import { LinkedCase, UpdateLinkedCasePayload, User } from 'src/app/state-management';
import Quill from 'quill';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'app-linked-cases',
	templateUrl: './linked-cases.component.html',
	styleUrls: ['./linked-cases.component.css'],
	standalone: false,
})
export class LinkedCasesComponent implements OnInit, OnChanges {
	@Input() linkedCases: LinkedCase[];
	@Input() usersList: User[];
	@Input() linkedStatus: string;
	@Input() problemNo: string;
	@Input() isBatchExport: boolean;
	@Input() isCityworksRequest: boolean;

	@Output() unlinkCaseEmitter = new EventEmitter<LinkedCase>();
	@Output() updateCaseNoteEmitter = new EventEmitter<UpdateLinkedCasePayload>();
	@Output() linkEGDialogEmitter = new EventEmitter<string>();
	@Output() editLinkedCaseEmitter = new EventEmitter();
	@Output() cityWorksRequestViewEmitter = new EventEmitter();

	quillEditForm: UntypedFormGroup;
	quillEditors: Array<{ key: string; editor: Quill }> = [];
	quillTextEntries: Array<{ key: string; value: string }> = [];
	quillFocusEntries: Array<{ key: string; value: boolean }> = [];
	activeEditor: any;
	@ViewChild(NgbDropdown)
	private dropdown: NgbDropdown;

	constructor(private fb: UntypedFormBuilder) {}

	ngOnInit(): void {}

	ngOnChanges(): void {
		if (this.linkedCases.length) {
			this.linkedCases = this.linkedCases
				.filter((linkedCase) => linkedCase.problemOneNumber === this.problemNo || linkedCase.problemTwoNumber === this.problemNo)
				.sort((a: LinkedCase, b: LinkedCase) => {
					const aTime: number = new Date(a.linkCreationDate).getTime();
					const bTime: number = new Date(b.linkCreationDate).getTime();
					return bTime - aTime;
				});
			if (!this.quillEditForm) {
				// only create form if undefined, otherwise notes content gets blanked out
				this.quillEditForm = this.createGroup();
			}
		}
	}

	createGroup(): UntypedFormGroup {
		const group = this.fb.group({});
		this.linkedCases.forEach((linkedCase) => {
			group.addControl(linkedCase.problemTwoNumber, this.fb.control(''));
		});
		return group;
	}

	unlink(linkedCase: LinkedCase): void {
		this.unlinkCaseEmitter.emit(linkedCase);
	}

	edit(linkedCase: LinkedCase): void {
		this.editLinkedCaseEmitter.emit(linkedCase);
	}

	getUserFromUserList(userId: number): string {
		if (this.usersList.length) {
			const linkCreator = this.usersList.filter((user) => user.user_Id === userId);
			return `${linkCreator[0].firstName} ${linkCreator[0].lastName}`;
		}

		return '';
	}

	getLinkedStatus(linkedCase: LinkedCase): string {
		if (linkedCase.problemOneNumber === this.problemNo && linkedCase.isRelated) {
			return 'Child';
		} else if (linkedCase.problemTwoNumber === this.problemNo && linkedCase.isRelated) {
			return 'Parent';
		} else {
			return 'Related';
		}
		// else if (!linkedCase.problemTwoNumber.includes('PRB') && /^\d+$/.test(linkedCase.problemTwoNumber)) {
		// 	// check if the problemTwoNumber value is a Cityworks entry
		// 	return 'Transfer';
		// }
	}

	getCityworksView(linkedCase: LinkedCase) {
		let cityWorksRequestId = Number(linkedCase.problemTwoNumber);
		this.cityWorksRequestViewEmitter.emit(cityWorksRequestId);
	}

	getProblemNo(linkedCase: LinkedCase): string {
		switch (this.linkedStatus) {
			case 'Parent':
				return linkedCase.isRelated
					? linkedCase.problemTwoNumber
					: linkedCase.problemOneNumber === this.problemNo
					? linkedCase.problemTwoNumber
					: linkedCase.problemOneNumber;

			case 'Child':
				return linkedCase.isRelated
					? linkedCase.problemOneNumber
					: linkedCase.problemOneNumber === this.problemNo
					? linkedCase.problemTwoNumber
					: linkedCase.problemOneNumber;

			case 'Unlinked':
			default:
				return linkedCase.problemOneNumber === this.problemNo ? linkedCase.problemTwoNumber : linkedCase.problemOneNumber;
		}
	}

	getProblemType(linkedCase: LinkedCase, caseLinkedStatus: string): string {
		switch (caseLinkedStatus) {
			case 'Parent':
				return linkedCase.problemOne.problemType_Name;

			case 'Child':
				return linkedCase.application === 'EnerGov' ? linkedCase.description : linkedCase.problemTwo.problemType_Name;

			case 'Unlinked':
			default:
				return linkedCase.problemOneNumber === this.problemNo
					? linkedCase.problemTwo_RefId
						? linkedCase.problemTwo.problemType_Name
						: linkedCase.description
					: linkedCase.problemOne.problemType_Name;
		}
	}

	openAddLinkedCase(linkedTo: string) {
		this.linkEGDialogEmitter.emit(linkedTo);
	}

	convertCaseNoToURL(linkedCase: LinkedCase): string {
		const webRoot = `https://${window.location.host}`;
		const problemURL = `${webRoot}/problemdetails?ProblemNo=${this.getProblemNo(linkedCase)}`;
		return problemURL;
	}

	onNotesEditorCreated(e: Quill, targetCase: LinkedCase) {
		let notes = targetCase.notes;
		if (notes === undefined || notes === null) {
			notes = '';
		}

		const kvPair = { key: targetCase.problemTwoNumber, editor: e };
		const editorIndex = this.quillEditors.findIndex((editor) => editor.key === targetCase.problemTwoNumber);

		if (editorIndex >= 0) {
			this.quillEditors[editorIndex] = kvPair;
		} else {
			this.quillEditors.push(kvPair);
		}

		const kvTextPair = { key: targetCase.problemTwoNumber, value: '' };
		if (notes.includes('<ul>') || notes.includes('<ol>')) {
			const delta = e.clipboard.convert({ text: notes });
			e.setContents(delta, 'silent');
			kvTextPair.value = e.root?.innerHTML;
		} else {
			e.setText(notes);
			kvTextPair.value = notes;
		}

		const textIndex = this.quillTextEntries.findIndex((textEntry) => textEntry.key === targetCase.problemTwoNumber);
		if (textIndex >= 0) {
			this.quillTextEntries[textIndex] = kvTextPair;
		} else {
			this.quillTextEntries.push(kvTextPair);
		}
	}

	performNoteUpdate(editor: Quill, targetCase: LinkedCase) {
		const innerHTML = editor.root?.innerHTML || '';
		const quillTextEntry = this.quillTextEntries.filter((entry) => entry.key === targetCase.problemTwoNumber)[0];
		quillTextEntry.value = innerHTML.includes('<ul>') || innerHTML.includes('<ol>') ? innerHTML : editor.getText();

		const formControl = this.quillEditForm.get(targetCase.problemTwoNumber);
		targetCase.focus = false;
		// targetCase.notes = quillTextEntry.value;

		if (!formControl.pristine) {
			const payload: UpdateLinkedCasePayload = {
				linkedCase_Id: targetCase.problemLinked_Id,
				updatedLinkedCase: {
					...targetCase,
					notes: quillTextEntry.value,
				},
				caseNo: this.problemNo,
			};

			formControl.markAsPristine();
			this.updateCaseNoteEmitter.emit(payload);
		}
	}

	updateNotes(targetCase: LinkedCase) {
		const editor = this.quillEditors.filter((qEditor) => qEditor.key === targetCase.problemTwoNumber)[0];
		this.performNoteUpdate(editor.editor, targetCase);
	}

	setNotesFocus(targetCase: LinkedCase) {
		targetCase.focus = true;
	}

	performUndoEditNote(editor: Quill, targetCase: LinkedCase) {
		const notes = this.quillTextEntries.filter((entry) => entry.key === targetCase.problemTwoNumber)[0];
		const noteText = notes.value;

		// if (noteText.includes('<ul>') || noteText.includes('<ol>')) {
		const delta = editor.clipboard.convert({ text: noteText });
		editor.setContents(delta, 'silent');
		// } else {
		// 	editor.setText(noteText);
		// }

		this.quillEditForm.get(targetCase.problemTwoNumber).markAsUntouched();
		targetCase.focus = false;
	}

	undoEditNotes(targetCase: LinkedCase) {
		const editor = this.quillEditors.filter((qEditor) => qEditor.key === targetCase.problemTwoNumber)[0];
		this.performUndoEditNote(editor.editor, targetCase);
	}
}
